import mapboxgl from 'mapbox-gl';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import type { Coordinate } from '@shared/models/geography.model';
import { canvasColors } from '../map.constants';
import type { PointData } from './types';

/** Size of the crosshair arms in meters (total width = 2 * arm length) */
const CROSSHAIR_ARM_LENGTH_M = 0.05;

/** Gray color for the default draft crosshair indicator and connecting line */
const CROSSHAIR_COLOR = '#888888';

/**
 * Build wireframe vertices for a crosshair indicator at the given position.
 * Renders as two perpendicular short lines forming an X shape,
 * plus an optional dashed line from a previous point.
 * In pen mode the rubber-band is a solid blue line and the X is omitted.
 * @param coordinate Geographic coordinate where the crosshair should appear
 * @param elevation Elevation in meters
 * @param originMercator Floating origin for Mercator calculations
 * @param lastPointCoordinate Optional coordinate of the last point for connecting line
 * @param penMode When true, draw the add-building rubber-band (DEV-2576)
 */
export function buildCrosshairVertices({
  coordinate,
  elevation,
  originMercator,
  lastPointCoordinate,
  penMode = false,
}: {
  coordinate: Coordinate;
  elevation: number;
  originMercator: mapboxgl.MercatorCoordinate;
  lastPointCoordinate?: Coordinate;
  penMode?: boolean;
}): PointData[] {
  const vertices: PointData[] = [];
  const color = hexToRgbArray(
    penMode ? canvasColors.lineBlue : CROSSHAIR_COLOR,
  );

  const center = mapboxgl.MercatorCoordinate.fromLngLat(
    { lng: coordinate.lng, lat: coordinate.lat },
    elevation,
  );

  // Approximate meters to Mercator units at this latitude
  const metersPerMercatorUnit = center.meterInMercatorCoordinateUnits();
  const armLength = CROSSHAIR_ARM_LENGTH_M * metersPerMercatorUnit;

  const cx = center.x - originMercator.x;
  const cy = center.y - originMercator.y;
  const cz = center.z - originMercator.z;

  if (lastPointCoordinate) {
    const lastMerc = mapboxgl.MercatorCoordinate.fromLngLat(
      { lng: lastPointCoordinate.lng, lat: lastPointCoordinate.lat },
      elevation,
    );
    const lx = lastMerc.x - originMercator.x;
    const ly = lastMerc.y - originMercator.y;
    const lz = lastMerc.z - originMercator.z;

    if (penMode) {
      pushCrosshairLine(
        vertices,
        { x: lx, y: ly, z: lz },
        { x: cx, y: cy, z: cz },
        color,
      );
    } else {
      pushDashedLine(
        vertices,
        { x: lx, y: ly, z: lz },
        { x: cx, y: cy, z: cz },
        color,
      );
    }
  }

  if (penMode) return vertices;

  // Build two diagonal lines forming an X
  // Line 1: from (-arm, -arm) to (+arm, +arm)
  pushCrosshairLine(
    vertices,
    { x: cx - armLength, y: cy - armLength, z: cz },
    { x: cx + armLength, y: cy + armLength, z: cz },
    color,
  );

  // Line 2: from (-arm, +arm) to (+arm, -arm)
  pushCrosshairLine(
    vertices,
    { x: cx - armLength, y: cy + armLength, z: cz },
    { x: cx + armLength, y: cy - armLength, z: cz },
    color,
  );

  return vertices;
}

/**
 * Push a dashed line segment from start to end.
 */
function pushDashedLine(
  vertices: PointData[],
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  color: [number, number, number],
): void {
  const [r, g, b] = color;
  const opacity = 1.0;

  // Calculate segment length for dashing
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  const segmentLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const addVertex = (
    curr: typeof start,
    other: typeof start,
    side: number,
    along: number,
    dist: number,
  ) => {
    vertices.push({
      x: curr.x,
      y: curr.y,
      z: curr.z,
      ox: other.x,
      oy: other.y,
      oz: other.z,
      side,
      along,
      r,
      g,
      b,
      opacity,
      distance: dist,
      isDashed: true,
      lineWidthScale: 1,
    });
  };

  // Tri 1
  addVertex(start, end, 1, -1, 0);
  addVertex(start, end, -1, -1, 0);
  addVertex(end, start, -1, 1, segmentLength);

  // Tri 2
  addVertex(end, start, -1, 1, segmentLength);
  addVertex(end, start, 1, 1, segmentLength);
  addVertex(start, end, 1, -1, 0);
}

/**
 * Push a single line segment for the crosshair.
 */
function pushCrosshairLine(
  vertices: PointData[],
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  color: [number, number, number],
): void {
  const [r, g, b] = color;
  const opacity = 1.0;

  const addVertex = (
    curr: typeof start,
    other: typeof start,
    side: number,
    along: number,
  ) => {
    vertices.push({
      x: curr.x,
      y: curr.y,
      z: curr.z,
      ox: other.x,
      oy: other.y,
      oz: other.z,
      side,
      along,
      r,
      g,
      b,
      opacity,
      distance: 0,
      isDashed: false,
      lineWidthScale: 1,
    });
  };

  // Tri 1
  addVertex(start, end, 1, -1);
  addVertex(start, end, -1, -1);
  addVertex(end, start, -1, 1);

  // Tri 2
  addVertex(end, start, -1, 1);
  addVertex(end, start, 1, 1);
  addVertex(start, end, 1, -1);
}
